import { caretDownIcon, caretRightIcon } from "../../elements/icons/index.js";
import { guid } from "../../helpers/string-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import SwitchButton from "../SwitchButton/SwitchButton.js";

/**
 * Display a list of items to be selected.
 *
 * Each item can be selected/unselected individually. Additionally, all
 * items of the list can be selected/unselected at once using the main
 * toggle button.
 *
 * @example
 *
 * new MultipleItemPickerWidget({
 * 	 title: _('Select items'),
 * 	 items: [
 * 		 {value: 'tomato', selected: true},
 * 		 {value: 'banana', selected: false},
 * 		 {value: 'strawberry', selected: false, disabled: true}
 * 	 ]
 * })
 * @param {Renderable} spec.title - Will be rendered above all items
 *   and next to the main toggle button
 * @param {Renderable} [spec.subtitle] - Will be rendered right below
 *   the title
 * @param {object[]} spec.items - List of items to choose from. Each
 *   item is an object with at least a `selected` property with a
 *   boolean value. An item with a `disabled` property set to true
 *   cannot be selected
 * @param {function} spec.itemRenderer - Function used to render each
 *   item
 * @param {boolean} [spec.disabled] - When true, no item of the list
 *   can be selected.
 * @param {boolean} [spec.collapsed] - Whether the list hides its items
 *   by default.
 * @param {boolean} [spec.allowCollapsing] - Whether the list shows a
 *   collapsing arrow
 */
export default class MultipleItemPickerWidget extends Widget {
	constructor({
		title,
		subtitle,
		items,
		itemRenderer,
		disabled = false,
		collapsed = false,
		allowCollapsing = true,
		itemVisibilityMatcher,
	}) {
		super(...arguments);
		this._title = title;
		this._subtitle = subtitle;
		this._disabled = disabled;
		this._itemRenderer = itemRenderer;
		this._collapsed = collapsed;
		this._allowCollapsing = allowCollapsing;
		this._itemVisibilityMatcher = itemVisibilityMatcher;

		this._initializeItems(items);

		this._mainSwitch = new SwitchButton({
			state: this._anyItemSelected(),
			disabled: this._disabled,
		});

		this._mainSwitch.stateChanged.register((state) => {
			const anyItemSelected = this._anyItemSelected();

			if (!state || !anyItemSelected) {
				this._items.forEach((item) => {
					item.selected = !item.disabled && state;
				});
			}

			this.update();
		});
	}

	toggleCollapsed() {
		this._collapsed = !this._collapsed;
		this.update();
	}

	selectedValues() {
		return this._items
			.filter((item) => item.selected)
			.map((item) => item.value);
	}

	renderContentOn(html) {
		html.div(
			{
				klass: ["multiple-item-picker", { disabled: this._disabled }],
			},
			this._renderTitleOn.bind(this),
			this._renderSubtitleOn.bind(this),
			this._renderListOn.bind(this),
		);
	}

	applySearchFrom(searchField) {
		return searchField.applySearchOnList(this);
	}

	updateSearch(searchString) {
		this._searchString = searchString;
		this.update();
	}

	_initializeItems(items) {
		this._items = items;

		this._items.forEach((item) => {
			if (!this._disabled && !item.disabled) {
				return;
			}
			item.selected = false;
		});
	}

	_renderTitleOn(html) {
		html.h3(
			this._renderTogglerOn.bind(this),
			html.span({ klass: "title" }, this._title),
			this._mainSwitch,
		);
	}

	_renderSubtitleOn(html) {
		if (!this._subtitle) {
			return;
		}
		html.h4(this._subtitle);
	}

	_renderTogglerOn(html) {
		if (!this._allowCollapsing) {
			return;
		}

		html.a(
			{
				klass: "toggler",
				"data-test": "toggle",
				click: this.toggleCollapsed.bind(this),
			},
			this._collapsed ? caretRightIcon : caretDownIcon,
		);
	}

	_renderListOn(html) {
		if (this._collapsed) {
			return;
		}

		html.ul(
			this._items
				.filter((item) => this._shouldRenderItem(item))
				.map((item) => this._listItemRenderer(item)),
		);
	}

	_shouldRenderItem(item) {
		return (
			!this._searchString ||
			this._itemVisibilityMatcher(this._searchString, item)
		);
	}

	_listItemRenderer(item) {
		return (html) =>
			html.li((html) => {
				const checkboxId = guid();
				const checkbox = html.input({
					type: "checkbox",
					id: checkboxId,
				});

				if (item.selected) {
					checkbox.setAttribute("checked", true);
				}

				if (this._isItemDisabled(item)) {
					checkbox.setAttribute("disabled", true);
				}

				checkbox.change(() => {
					item.selected = checkbox.element.checked;
					this._mainSwitch.setState(this._anyItemSelected());

					// The update() call below allows for dynamic titles
					// and subtitles that show the number of selected
					// items:
					this.update();
				});

				html.label({ for: checkboxId }, this._itemRenderer(item));
			});
	}

	_isItemDisabled(item) {
		return this._disabled || item.disabled;
	}

	_anyItemSelected() {
		return this._items.some((item) => item.selected);
	}
}
